import React, { useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import ConfirmDialog from '../ConfirmDialog';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import MaterialTable from 'material-table'

import AvailabilityForm from './AvailabilityForm';


const tableRef = React.createRef();
const sessionKeys = {
    search: 'enduringpromises.com.availability.table.search'
};

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(3),
    },
    dialogTitle: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
    },
    dialogActions: {
        justifyContent: 'center'
    },
    dialogButton: {
        minWidth: 200,
        maxWidth: '100%'
    },
    formWrapper: {
        '& form': {
            maxWidth: 650,
            marginLeft: 'auto',
            marginRight: 'auto'
        }
    },
}));

const MaterialTableTable = () => {
    const classes = useStyles();

    const isAdmin = window.isAdmin;
    const isLimitedAdmin = window.isLimitedAdmin;
    const columns = [
        {
            title: 'Start Date',
            field: 'start_str',
        },
        {
            title: 'End Date',
            field: 'end_str',
        },
        {
            title: 'Duration',
            field: 'duration',
        },
        {
            title: 'Note',
            field: 'note'
        },
        {
            title: 'Created',
            field: 'created_str'
        },
        {
            title: 'Updated',
            field: 'updated_str'
        }
    ];

    if (isAdmin || isLimitedAdmin) {
        columns.push(
            {
                title: 'User',
                field: 'user.name'
            }
        )
    }

    const [state, setState] = React.useState({ columns });

    const pageSizeOptions = [30, 50, 100, 250, 500];
    const [resultTotal, setResultTotal] = React.useState(0);
    const filteredPageSizeOptions = total => total > 0 ? pageSizeOptions : [0];

    const fetchData = query =>
        new Promise((resolve, reject) => {
            let url = '/api/unavailability?';

            /* set the search to local storage */
            localStorage.setItem(sessionKeys.search, query.search);

            /* TODO: use orderBy and filters */
            url += 'per_page=' + query.pageSize
            url += '&page=' + (query.page + 1)
            url += '&search=' + query.search
            url += '&orderBy=' + (query.orderBy ? query.orderBy.field : '')
            url += '&orderDirection=' + query.orderDirection
            if (query.filters.length) {
                url += '&' + query.filters.map(x => `${x.column.field}=`+encodeURI(x.value)).join('&')
            }
            fetch(url)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setResultTotal(result.total);
                    resolve({
                        data: result.data,
                        page: result.current_page - 1,
                        totalCount: result.total,
                    })
                })
            }
        )

    const history = useHistory();
    const [selected, setSelected] = React.useState({});

    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const deleteDate = selected => {
        const data = { _token: window._token };
        new Promise((resolve, reject) => {
            let url = `/api/unavailability/${selected.id}`;
            fetch(url, {
                method: 'DELETE',
                body: JSON.stringify(data),
                headers: { 'Content-Type': 'application/json' }
            })
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setSnackbarMessage('Unavailability was successfully deleted');
                    setOpenSnackbar(true);
                    resolve();
                })
                .catch(reason => {
                    alert('An error prevented deleting this date. Please refresh the page and try again.');
                })
        });
    }

    const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
    const handleCloseDialog = action => () => {
        if (action == 'agree') {
            deleteDate(selected);
            tableRef.current && tableRef.current.onQueryChange()
        }
        setConfirmDialogOpen(false);
    };

    const handleEditDate = (event, rowData) => {
        const newUrl = `/admin/officiants/availability/${rowData.id}`;
        if (event.metaKey) {
            window.open(newUrl);
            return;
        }
        history.push(newUrl);
    }

    const handleDeleteDate = (event, rowData) => {
        setSelected(rowData);
        setConfirmDialogOpen(true);
    };

    const defaultSearchText = localStorage.getItem(sessionKeys.search) || '';

    return (
        <React.Fragment>
            <MaterialTable
                title="Unavailability List"
                columns={state.columns}
                data={fetchData}
                actions={[
                    {
                        icon: 'edit',
                        tooltip: 'Edit Dates',
                        onClick: handleEditDate
                    },
                    {
                        icon: 'delete',
                        tooltip: 'Delete',
                        onClick: handleDeleteDate
                    },
                    {
                        icon: 'refresh',
                        tooltip: 'Refresh Data',
                        isFreeAction: true,
                        onClick: () => tableRef.current && tableRef.current.onQueryChange(),
                    }
                ]}
                components={{
                    Container: Box,
                }}
                options={{
                    showTitle: false,
                    pageSize: 30,
                    pageSizeOptions: filteredPageSizeOptions(resultTotal),
                    exportButton: true,
                    loadingType: 'linear',
                    emptyRowsWhenPaging: false,
                    sorting: false,
                    debounceInterval: 600,
                    padding: 'dense',
                    searchText: defaultSearchText,
                }}
                tableRef={tableRef}
            />
            <ConfirmDialog
                title="Confirm delete"
                text="Are you sure you wish to delete this unavailability date?"
                agreeText="Delete"
                declineText="Cancel"
                handleClose={handleCloseDialog}
                open={confirmDialogOpen}
                setOpen={setConfirmDialogOpen}
                agreeColor="secondary"
            />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbarMessage}
                action={
                    <React.Fragment>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </React.Fragment>
                }
            />
        </React.Fragment>
    );
};

const AvailabilityDialog = (props) => {
    const {
        open,
        handleClose,
        scroll,
        handleSave,
    } = props;

    const classes = useStyles();

    const handleSaveButtonClick = () => {
        handleSave();
        handleClose();
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='lg'
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>Add Unavailable Date</DialogTitle>
            <DialogContent dividers={scroll === 'paper'}>
                <div className={classes.formWrapper}>
                    <AvailabilityForm handleSaveButtonClick={handleSaveButtonClick} />
                </div>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button onClick={handleClose} variant="outlined" className={classes.dialogButton}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const UserAvailability = () => {
    const classes = useStyles();

    useEffect(() => {
        document.title = 'Availability - Enduring Promises';
    });

    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const [open, setOpen] = React.useState(false);
    const openDialog = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        tableRef.current.onQueryChange();
        setSnackbarMessage('Successfully added new unavailable date');
        setOpenSnackbar(true);
    }

    return (
        <Paper square className={classes.paper}>
            <div style={{ maxWidth: '100%' }}>
                <Typography variant="h6">Availability - Unavailable Dates</Typography>
                <Grid
                    container
                    spacing={2}
                    direction="row"
                    justify="space-between"
                    alignItems="center"
                >
                    <Grid item xs={12} sm md style={{textAlign: 'right', minWidth: 142}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={openDialog}
                        >
                            New Unavailable Date
                        </Button>
                    </Grid>
                </Grid>
                <MaterialTableTable />
                <AvailabilityDialog open={open} handleClose={handleClose} scroll="paper" handleSave={handleSave} />
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    open={openSnackbar}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    message={snackbarMessage}
                    action={
                        <React.Fragment>
                            <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </React.Fragment>
                    }
                />
            </div>
        </Paper>
    )
};

export default UserAvailability;
